import { Availability } from '../domain/Availability'
import { BookingDetails } from '../domain/BookingDetails'
import { BookingRequest } from '../domain/BookingRequest'
import { BookingRequestFactory } from '../domain/BookingRequestFactory'
import { BookingSlot } from '../domain/BookingSlot'
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core'
import { ContextService } from '../services/ContextService'
import { FormControl, FormGroup, FormGroupDirective, Validators } from '@angular/forms'
import { FormStep } from '../booking-form/booking-form.component'
import { HttpErrorResponse } from '@angular/common/http'
import { NGBDateTimeAdapter } from '../utilities/NgbDateTimeAdapter'
import { NgbDateNativeAdapter } from '@ng-bootstrap/ng-bootstrap'
import { PaymentSession, PaymentSessionValidationError } from '../services/PaymentSession'
import { PaymentsService } from '../services/payments.service'
import { RouterContextService } from '../services/RouterContextService'
import { Subject } from 'rxjs'
import { Venue } from '../domain/Venue'
import DineroFactory, { Currency, Dinero } from 'dinero.js'
import { LoggingService } from '../services/logging.service'
import { v4 as uuidv4 } from 'uuid'
import { PaymentIntent } from '../domain/PaymentIntent'

@Component({
    selector: 'app-payment-details-form',
    templateUrl: './payment-details-form.component.html',
    styleUrls: ['./payment-details-form.component.sass'],
    providers: [{
        provide: ContextService,
        useClass: RouterContextService,
    }],
})
export class PaymentDetailsFormComponent implements OnInit, OnDestroy {

    @Input() showTitle = false
    @Input() showBackButton = false
    @Input() venue?: Venue
    @Input() bookingDetails?: BookingDetails
    @Input() utmSource?: string
    @Input() utmMedium?: string
    @Input() utmCampaign?: string
    @Output() backSelected = new EventEmitter<void>()
    @Output() paymentStarted = new EventEmitter<string>()
    @Output() paymentFailed = new EventEmitter<void>()
    @Output() bookSelected = new EventEmitter<void>()
    @Output() cardDetailsSkipped = new EventEmitter<void>()
    form!: FormGroup
    cancellationChargeAmount!: Dinero
    cancellationChargeIsPerCover!: boolean
    cancellationChargeCutOffHours: number | null = null
    paymentDetailsFormError: string | null = null
    bookingSlotUnavailableWarning: string | null = null
    hoursPluralMapping = {
        '=0': '# hours',
        '=1': '# hour',
        other: '# hours',
    }
    isSaving = false
    cardDetailsAreValid = false
    paymentFormIsReady = false
    usesHostedCheckout = false
    requiresFutureChargeConsent = false
    walletPaymentAvailable = false
    billingEmailAddressControl = new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
    })
    merchantInitiatedTransactionConsentControl = new FormControl(false, {
        nonNullable: true,
        validators: [Validators.requiredTrue],
    })
    private paymentSession: PaymentSession | null = null
    private paymentIntent: PaymentIntent | null = null
    private destroyed = false
    private ngbDateAdapter = new NgbDateNativeAdapter()
    private ngbTimeAdapter = new NGBDateTimeAdapter()
    private onDestroy$ = new Subject<void>()

    get isRyftPayment(): boolean {
        return this.paymentSession?.gateway === 'ryftpay'
    }

    get shouldCollectCustomerEmail(): boolean {
        return this.bookingDetails !== undefined
    }

    get fullCancellationChargeAmount(): string {
        const partySize = this.bookingDetails?.partySize
            ?? this.rootFormGroup.control.get('BookingSlot.partySize')?.value
            ?? 1
        return this.cancellationChargeAmount
            .multiply(this.cancellationChargeIsPerCover ? partySize : 1)
            .toFormat()
    }

    constructor(
        private rootFormGroup: FormGroupDirective,
        private contextService: ContextService,
        private loggingService: LoggingService,
        private paymentsService: PaymentsService,
        private changeDetectorRef: ChangeDetectorRef
    ) { }

    ngOnInit() {
        let formName = FormStep[FormStep.PaymentDetails]
        this.form = this.rootFormGroup.control.get(formName) as FormGroup
        this.billingEmailAddressControl.setValue(this.bookingDetails?.bookingEmailAddress ?? '')
        this.setUpChargeDetails()
    }

    ngOnDestroy() {
        this.destroyed = true
        this.paymentSession?.destroy()
        this.paymentSession = null
        this.paymentIntent = null
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    onBackSelected() {
        this.backSelected.emit()
    }

    async onBookSelected(sessionId?: string) {
        if ((this.usesHostedCheckout || this.requiresFutureChargeConsent) && this.merchantInitiatedTransactionConsentControl.invalid) {
            this.merchantInitiatedTransactionConsentControl.markAsTouched()
            return
        }
        this.isSaving = true
        this.paymentStarted.emit(this.paymentIntent?.id)
        try {
            await this.savePaymentDetails(sessionId)
            this.bookSelected.emit()
        } catch (error) {
            if (this.destroyed) {
                return
            }
            this.paymentFailed.emit()
            if (error instanceof PaymentSessionValidationError) {
                return
            }
            const defaultErrorMessage = 'There was an error saving your payment details. ' +
                'Please refresh the page and try again.'
            const message = error instanceof Error || error instanceof HttpErrorResponse
                ? error.message
                : defaultErrorMessage
            this.loggingService.noticeError(error instanceof Error ? error : new Error(message))
            if (error instanceof HttpErrorResponse && error.status === 404) {
                this.bookingSlotUnavailableWarning = 'Sorry, this booking slot is no longer available. Please select another time.'
            } else {
                this.paymentDetailsFormError = message
            }
        } finally {
            this.isSaving = false
        }
    }

    clearFormErrors() {
        this.paymentDetailsFormError = null
        this.bookingSlotUnavailableWarning = null
    }

    private setUpChargeDetails() {
        if (this.venue !== undefined && this.bookingDetails !== undefined) {
            this.setUpChargeDetailsFromBookingDetails(this.venue, this.bookingDetails)
        } else {
            this.setUpChargeDetailsFromForm()
        }
    }

    private setUpChargeDetailsFromBookingDetails(venue: Venue, bookingDetails: BookingDetails) {
        const cancellationChargeAmount = bookingDetails.cancellationChargeAmount
        if (cancellationChargeAmount === null) {
            return
        }
        this.cancellationChargeAmount = DineroFactory({
            amount: cancellationChargeAmount,
            currency: venue.currencyCode as Currency,
        })
        this.cancellationChargeIsPerCover = false
        this.cancellationChargeCutOffHours = venue.cancellationChargeCutOffHours
        this.mountPendingPaymentSetupPaymentDetailsElement(venue, bookingDetails)
            .catch(error => {
                if (error instanceof HttpErrorResponse && error.status === 404) {
                    this.bookingSlotUnavailableWarning = 'Sorry, this booking slot is no longer available. Please select another time.'
                } else {
                    this.paymentDetailsFormError =
                        'There was an error setting up your booking. ' +
                        'Please refresh the page and try again.'
                }
            })
    }

    private setUpChargeDetailsFromForm() {
        const bookingSlotForm = this.rootFormGroup.control.get('BookingSlot')
        const availabilityControl = bookingSlotForm?.get('availability')
        const availability = availabilityControl?.value as Availability | null
        if (availability === null) {
            return
        }
        const venue: Venue = this.venue
            ?? availability?.venue
        if (venue === null) {
            return
        }
        const areaId: string = bookingSlotForm?.get('selectedAreaId')?.value as string
        if (areaId === null) {
            return
        }
        let bookingSlot: BookingSlot = bookingSlotForm?.get('bookingSlot')?.value
        if (bookingSlot === undefined) {
            const ngbDate = bookingSlotForm?.get('date')?.value
            const ngbTime = bookingSlotForm?.get('time')?.value
            const date = this.ngbDateAdapter.toModel(ngbDate)!
            const time = this.ngbTimeAdapter.toModel(ngbTime)!
            const dateTime = availability.getDateTimeForBookingSlotDateAndTime(
                date,
                time
            )
            bookingSlot = new BookingSlot(dateTime)
        }
        if (bookingSlot === null) {
            return
        }
        const bookingSlotOption = availability
            .reservableOptionWithConfiguration(
                bookingSlotForm?.get('selectedAreaId')?.value,
                bookingSlotForm?.get('partySize')?.value,
                bookingSlotForm?.get('selectedReasonId')?.value,
                bookingSlotForm?.get('selectedEventId')?.value,
                bookingSlot.dateTime
            )
        if (bookingSlotOption === null) {
            return
        }
        this.cancellationChargeAmount = DineroFactory({
            amount: bookingSlotOption.cancellationChargeAmount!,
            currency: venue.currencyCode as Currency,
        })
        this.cancellationChargeIsPerCover = bookingSlotOption.cancellationChargeIsPerCover
        this.cancellationChargeCutOffHours = bookingSlotOption.cancellationChargeCutOffHours
        const bookingRequestId = uuidv4()
        bookingSlotForm?.patchValue({ 'bookingRequestId': bookingRequestId })
        const factory = new BookingRequestFactory(this.rootFormGroup.control, this.utmSource ?? null, this.utmMedium ?? null, this.utmCampaign ?? null)
        const bookingRequest = factory.make()
        this.mountSetUpPaymentDetailsElement(venue, areaId, bookingRequest)
            .catch(error => {
                if (error instanceof HttpErrorResponse && error.status === 404) {
                    this.bookingSlotUnavailableWarning = 'Sorry, this booking slot is no longer available. Please select another time.'
                } else {
                    this.paymentDetailsFormError =
                        'There was an error setting up your booking. ' +
                        'Please refresh the page and try again.'
                }
            })
    }

    private async mountSetUpPaymentDetailsElement(
        venue: Venue,
        areaId: string,
        bookingRequest: BookingRequest
    ) {
        const payment = await this.paymentsService.makeSetupIntentClientSecret(
            venue.organisationId,
            venue.businessId,
            venue.id,
            areaId,
            bookingRequest
        )
        // If payment is null, customer is exempt from card details
        if (payment === null) {
            this.cardDetailsSkipped.emit()
            return
        }
        await this.startPaymentSession(venue, payment)
    }

    private async mountPendingPaymentSetupPaymentDetailsElement(
        venue: Venue,
        bookingDetails: BookingDetails
    ) {
        const payment = await this.paymentsService.makePendingPaymentSetupIntentClientSecret(
            venue.organisationId,
            venue.businessId,
            bookingDetails
        )
        await this.startPaymentSession(venue, payment)
    }

    private async startPaymentSession(
        venue: Venue,
        intent: PaymentIntent
    ) {
        if (this.destroyed) {
            return
        }
        this.cardDetailsAreValid = false
        this.paymentFormIsReady = false
        this.usesHostedCheckout = false
        this.changeDetectorRef.detectChanges()
        // Allow Angular to render the mount targets before we attach the SDK to them.
        await Promise.resolve()
        if (this.destroyed) {
            return
        }
        const paymentSession = await this.paymentsService.createPaymentSession(venue, {
            intent,
            usage: 'setup-card',
            hasFutureChargeConsent: () => this.merchantInitiatedTransactionConsentControl.valid,
            cardFormSelector: '#payment-card-form',
            requiresBillingEmail: this.shouldCollectCustomerEmail,
            getBillingEmailAddress: this.shouldCollectCustomerEmail
                ? () => this.validBillingEmailAddress()
                : undefined,
            applePayWalletButtonSelector: '#payment-apple-wallet-button',
            googlePayWalletButtonSelector: '#payment-google-wallet-button',
        })
        if (this.destroyed) {
            paymentSession.destroy()
            return
        }
        this.paymentSession = paymentSession
        this.paymentIntent = intent
        this.walletPaymentAvailable = paymentSession.walletPaymentAvailable
        this.usesHostedCheckout = paymentSession.usesHostedCheckout === true
        this.requiresFutureChargeConsent = this.usesHostedCheckout || paymentSession.requiresFutureChargeConsent === true
        this.paymentFormIsReady = true
        this.changeDetectorRef.detectChanges()
        paymentSession.setOnValidityChange((isValid) => this.updateCardValidationState(isValid))
        paymentSession.setOnWalletConfirmed((intentId) => this.onBookSelected(intentId))
        paymentSession.setOnError((message) => {
            this.paymentDetailsFormError = message
                ?? 'There was an error setting up the payment form. ' +
                'Please refresh the page and try again.'
            this.changeDetectorRef.detectChanges()
        })
    }

    private async savePaymentDetails(setupIntentId?: string) {
        const billingEmailAddress = this.validBillingEmailAddress()
        if (this.shouldCollectCustomerEmail && billingEmailAddress === undefined) {
            throw new PaymentSessionValidationError('Please enter a valid email address.')
        }
        let id = setupIntentId
        if (!id) {
            if (!this.paymentSession) {
                throw new Error('Please refresh the page and try again')
            }
            id = await this.paymentSession.confirm(billingEmailAddress)
        }
        const paymentIntent = this.paymentIntent
        if (paymentIntent === null || id !== paymentIntent.id) {
            throw new Error('The confirmed payment did not match the current payment.')
        }
        this.form.patchValue({ 'setupIntentId': id })
    }

    private validBillingEmailAddress(): string | undefined {
        if (!this.shouldCollectCustomerEmail) {
            return undefined
        }
        this.billingEmailAddressControl.markAsTouched()
        this.changeDetectorRef.detectChanges()
        if (this.billingEmailAddressControl.invalid) {
            return undefined
        }
        return this.billingEmailAddressControl.value.trim()
    }

    private updateCardValidationState(isValid: boolean) {
        this.cardDetailsAreValid = isValid
        this.changeDetectorRef.detectChanges()
    }
}
